import { Location } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { filter, skipWhile, take } from 'rxjs/operators';

import { selectCurrentUser } from 'src/app/store/auth/auth.selectors';

import { GisService } from '../../../../core/api/gis.service';
import { RegionService } from '../../../../core/api/region.service';
import { SiteService } from '../../../../core/api/site.service';
import { SiteHelperService } from '../../../../core/helper/site.service';
import { DialogOutOfRegionComponent } from '../../../../shared/dialogs/dialog-out-of-region/dialog-out-of-region.component';
import { DialogYesNoComponent } from '../../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Parcel, Site, User } from '../../../../shared/models';
import { selectAppConfigState } from '../../../../store/app-config/app-config.selectors';
import * as fromApp from '../../../../store/index';
import { selectForestryKml, selectSelectedParcels } from '../../../../store/parcel/parcel.selectors';
import { setActiveSite, setActiveSiteSelectedParcels, updateSiteFailure } from '../../../../store/site/site.actions';
import { selectReadOnlyMapInfo } from '../../../../store/site/site.selectors';

@Component({
  selector: 'fc-select-parcels',
  templateUrl: './select-parcels.component.html',
  styleUrls: ['./select-parcels.component.scss'],
})
export class SelectParcelsComponent implements OnInit {
  @ViewChild('documentUpload', { static: false }) documentUpload: ElementRef;
  @Input() siteId: number;
  @Input() activeSite: Site;

  destroyRef = inject(DestroyRef);

  selectedParcels$: Observable<Parcel[]>;
  originalParcels: Parcel[] = [];
  parcels: Parcel[];
  kmlUploaded = false;
  readOnlyMap: boolean;
  contactEmail: string = '';
  contactPhone: string = '';
  isFormDirty: boolean = false;
  currentUser: User = null;
  isAdmin: boolean = false;

  constructor(
    public dialog: MatDialog,
    readonly regionService: RegionService,
    readonly store: Store<fromApp.AppState>,
    readonly siteHelper: SiteHelperService,
    readonly siteService: SiteService,
    readonly route: ActivatedRoute,
    readonly router: Router,
    private gisService: GisService,
    private location: Location
  ) {
    this.selectedParcels$ = store.select(selectSelectedParcels).pipe(
      skipWhile(() => this.activeSite === null),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  ngOnInit() {
    // force window to scroll to top of screen to show all tools on mobile + tablet
    // no effect on desktop
    window.scrollTo(0, 1);

    this.selectedParcels$.subscribe(parcels => {
      this.parcels = parcels;
      if (this.originalParcels.length == 0) {
        this.originalParcels = parcels;
      }
      this.isFormDirty = this.parcels.length > 0;
    });

    this.store
      .select(selectForestryKml)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(forestryKml => {
        this.kmlUploaded = forestryKml && forestryKml.length > 0;
      });

    this.store
      .select(selectCurrentUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.currentUser = user;
      });

    this.store
      .select(selectReadOnlyMapInfo)
      .pipe(
        filter(() => this.currentUser !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(readOnlyInfo => {
        this.readOnlyMap = !this.siteHelper.canSaveMap(readOnlyInfo);
        this.isAdmin = readOnlyInfo.isAdmin;
        if (this.readOnlyMap) {
          this.isFormDirty = false;
        }
      });

    this.store
      .select(selectAppConfigState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(appConfig => {
        this.contactEmail = appConfig.config.fc_help_email;
        this.contactPhone = appConfig.config.fc_help_phone;
      });
  }

  checkRegions() {
    this.regionService.getIncluded(this.parcels).subscribe(parcelsLLUUIDs => {
      // if there are any parcels not in a valid region show which need to be
      // removed before the site can advance. mark the regions as not in a region
      const data = this.parcels.map(p => {
        const clonedParcel = cloneDeep(p);
        clonedParcel.inRegion = parcelsLLUUIDs.includes(clonedParcel.llUuid);
        return clonedParcel;
      });
      // Save list of selected parcels to state
      this.store.dispatch(
        setActiveSiteSelectedParcels({
          parcels: cloneDeep(this.parcels),
        })
      );
      const oorParcels = data.filter(p => !p.inRegion).map(p => p.parcelNumber);
      if (oorParcels.length > 0) {
        // show the dialog
        this.dialog.open(DialogOutOfRegionComponent, {
          panelClass: 'fc-dialog-out-of-region',
          data: { parcels: oorParcels },
        });
      } else {
        this.isFormDirty = false;
        if (this.isAdmin) {
          // have parcels changed?
          if (
            JSON.stringify([...new Set(this.parcels.map(obj => obj.llUuid))]) !==
            JSON.stringify([...new Set(this.originalParcels.map(obj => obj.llUuid))])
          ) {
            this.isFormDirty = false;
            // show confirmation box
            this.dialog
              .open(DialogYesNoComponent, {
                panelClass: 'fc-yes-no-dialog',
                data: {
                  title: 'Please Confirm',
                  line1: `By Saving these parcels, you will cause the site to change to Requires Landowner Approval.`,
                  line2: `You can continue to edit the site on the stand selection page before sending it back to the landowner.`,
                  buttonTextYes: 'Confirm',
                  buttonTextNo: 'Cancel',
                },
                autoFocus: false,
              })
              .afterClosed()
              .subscribe(result => {
                if (result === true) {
                  this.siteService
                    .updateSiteParcels(this.activeSite.id, this.parcels)
                    .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                    .subscribe(
                      site => {
                        this.siteHelper.askForApprovalEmail(
                          this.activeSite.id,
                          'Do you wish to send an email requesting approval now or after stand updates?',
                          'Stand Selection',
                          `admin/${this.activeSite.id}/map-site`
                        );
                      },
                      error => this.store.dispatch(updateSiteFailure({ error }))
                    );
                }
              });
          } else {
            // reset active site and redirect to map
            this.store.dispatch(setActiveSite({ id: this.siteId }));
            this.router.navigate([`admin/${this.siteId}/map-site`]);
          }
        } else {
          this.router.navigate([`site/${this.siteId}/locate-property/eligibility-check`], { skipLocationChange: true });
        }
      }
    });
  }

  handleBackLink(): void {
    this.location.back();
  }
}
